import { Key, useCallback } from "react";

import {
  Box,
  Button,
  ComboBox,
  ComboBoxContent,
  ComboBoxMenu,
  ComboBoxTrigger,
  MenuItem,
  MenuItemDescription,
  MenuItemLabel,
  Tooltip,
  Typography,
  useComboBox,
} from "@aircall/tractor";
import styled from "@xstyled/styled-components";
import { useTranslation } from "react-i18next";

import { SpeedPickerType } from "./SpeedPicker.decl";

import { speedOptions } from "@/components/PlayerSpeedControls/PlayerSpeedControls.constants";
import { DELAY } from "@/constants/tooltip.constants";

const StyledComboBoxTrigger = styled(ComboBoxTrigger)`
  & > button {
    padding: 0;
  }
`;

type SelectedSpeedProps = {
  disabled?: boolean;
};

function SelectedSpeed({ disabled }: SelectedSpeedProps) {
  const {
    selectedKeys: [firstKey],
  } = useComboBox();
  const { label } = speedOptions.find((s) => s.val === firstKey)!;

  return (
    <StyledComboBoxTrigger data-test="speed-picker">
      <Box justifySelf="left">
        <Button
          size="small"
          variant="secondary"
          mode="ghost"
          disabled={disabled}
        >
          {label}
        </Button>
      </Box>
    </StyledComboBoxTrigger>
  );
}

export function SpeedPicker({ value, onChange, disabled }: SpeedPickerType) {
  const { t } = useTranslation();
  const onSelectionChange = useCallback(
    (keys: Key[]) => {
      onChange(keys[0] as number);
    },
    [onChange]
  );

  return (
    <Tooltip mouseEnterDelay={DELAY} title={t("speed_control.label")}>
      <Box>
        <ComboBox
          selectedKeys={[value]}
          selectionMode="single"
          onSelectionChange={onSelectionChange}
        >
          <SelectedSpeed disabled={disabled} />
          <ComboBoxContent
            takeTriggerWidth={false}
            data-test="speed-picker-content"
          >
            <Typography variant="bodySemiboldS" p="8px 16px">
              {t("speed_control.label")}
            </Typography>
            <ComboBoxMenu>
              {speedOptions.map(({ val, description, label }) => (
                <MenuItem itemKey={val} key={val}>
                  <MenuItemLabel>{label}</MenuItemLabel>
                  {description && (
                    <MenuItemDescription>{t(description)}</MenuItemDescription>
                  )}
                </MenuItem>
              ))}
            </ComboBoxMenu>
          </ComboBoxContent>
        </ComboBox>
      </Box>
    </Tooltip>
  );
}
